import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDownIcon, PhoneIcon } from 'lucide-react';
import { photos } from '../../data/site';
const ease = [0.23, 1, 0.32, 1] as const;
const head = 'TÉLÉP'.split('');
const tail = 'HOMME'.split('');
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', '-40%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section ref={ref} className="relative flex min-h-[100svh] w-full flex-col justify-end overflow-hidden bg-ink" data-id="element-101" data-mp="1v4esmrzwchpl">
      <motion.img src={photos.crowdNight} alt="Téléphomme sur scène devant une foule brandissant ses téléphones allumés" style={{
      y: bgY,
      scale: bgScale
    }} className="absolute inset-0 h-full w-full object-cover object-[50%_40%]" data-id="element-102" data-mp="1q4shqp7jjdjy" />
      <div aria-hidden className="absolute inset-0 bg-ink/65" data-id="element-103" data-mp="1rt2r0wh7cakm" />

      <motion.div style={{
      y: titleY,
      opacity: fade
    }} className="relative mx-auto w-full max-w-7xl px-5 pb-10 pt-32 md:px-8 md:pb-14" data-id="element-104" data-mp="1czg9gndx1t45">
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease
      }} className="max-w-md text-base text-paper/80 md:text-lg" data-id="element-105" data-mp="1hz2ngz1kjde0">
          Le tribute consacré à <strong className="font-semibold text-paper" data-id="element-106" data-mp="1vw96muq7fe3r">Téléphone</strong>. Quatre musiciens,
          toute l’énergie du rock français des années 80.
        </motion.p>

        <h1 aria-label="Téléphomme" className="mt-4 select-none font-display text-[17vw] uppercase leading-[0.82] tracking-tight xl:text-[15.5rem]" data-id="element-107" data-mp="2ol6avtxkzadi">
          {[...head, ...tail].map((letter, i) => <span key={i} aria-hidden className="inline-block overflow-hidden align-bottom" data-id="element-108" data-mp="3373m760kes3n" data-mpi={i}>
              <motion.span className={`inline-block ${i >= head.length ? 'text-signal' : 'text-paper'}`} initial={{
            y: '105%'
          }} animate={{
            y: '0%'
          }} transition={{
            duration: 0.3,
            delay: 0.2 + i * 0.045,
            ease
          }} data-id="element-109" data-mp="2yalm4jg6jhxn">
                {letter}
              </motion.span>
            </span>)}
        </h1>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.75,
        ease
      }} className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-110" data-mp="2fhri2kk3ha87">
          <div className="flex flex-wrap gap-3" data-id="element-111" data-mp="21j1huw1zaufm">
            <Link to="/contact" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-signal px-7 py-3.5 font-semibold text-paper transition-[background-color,transform] duration-150 hover:bg-signal-dark active:scale-[0.97]" data-id="element-112" data-mp="21ii29lzswi4r">
              <PhoneIcon className="h-4 w-4" data-id="element-113" data-mp="gzpf2alej9kl" /> Booker le groupe
            </Link>
            <a href="#live" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-paper/30 px-7 py-3.5 font-semibold text-paper transition-[background-color,transform] duration-150 hover:bg-paper/10 active:scale-[0.97]" data-id="element-114" data-mp="lztyo9hlwkf1">
              Voir le live
            </a>
          </div>
          <dl className="flex gap-8 text-sm" data-id="element-115" data-mp="3udsoetdzj9k3">
            <div data-id="element-116" data-mp="2buiuk86qn2r">
              <dt className="text-paper/60" data-id="element-117" data-mp="2bb4fdhqugth">Depuis</dt>
              <dd className="font-display text-2xl" data-id="element-118" data-mp="72kqk443320m">2012</dd>
            </div>
            <div data-id="element-119" data-mp="74nqbfv1g9ij">
              <dt className="text-paper/60" data-id="element-120" data-mp="36qkm8kbusgma">Concerts</dt>
              <dd className="font-display text-2xl" data-id="element-121" data-mp="36pszlz08ht85">200+</dd>
            </div>
            <div data-id="element-122" data-mp="3aquuk3n4bj4b">
              <dt className="text-paper/60" data-id="element-123" data-mp="3aqcvkz92mqsp">Show</dt>
              <dd className="font-display text-2xl" data-id="element-124" data-mp="2rmrlhbr2c2ij">1h30</dd>
            </div>
          </dl>
        </motion.div>
      </motion.div>

      <RotatingBadge data-id="element-125" data-mp="2mpj0l3ej5kdl" />

      <a href="#intro" aria-label="Défiler vers la suite" className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-paper/60 md:block" data-id="element-126" data-mp="2mq13ujzm0yv0">
        <motion.span className="block" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-127" data-mp="2wlm4oesxzt36">
          <ArrowDownIcon className="h-5 w-5" data-id="element-128" data-mp="35fh9flq5jhia" />
        </motion.span>
      </a>
    </section>;
}
function RotatingBadge() {
  return <Link to="/contact" aria-label="Booker Téléphomme" className="group absolute right-8 top-28 hidden h-36 w-36 lg:block" data-id="element-129" data-mp="3kb6zbuygkesa">
      <motion.svg viewBox="0 0 100 100" className="h-full w-full" animate={{
      rotate: 360
    }} transition={{
      duration: 16,
      repeat: Infinity,
      ease: 'linear'
    }} aria-hidden data-id="element-130" data-mp="1pxnxwa1b4c1p">
        <defs data-id="element-131" data-mp="gjxcdxemauk">
          <path id="badge-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" data-id="element-132" data-mp="i4qbz42llqb" />
        </defs>
        <text className="fill-paper font-sans text-[9.5px] font-semibold uppercase tracking-[0.2em]" data-id="element-133" data-mp="5fdc09ofg7wb">
          <textPath href="#badge-circle" data-id="element-134" data-mp="3mvv65i48phpq">Allô ? · On joue chez vous · Allô ? · </textPath>
        </text>
      </motion.svg>
      <span className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-signal transition-transform duration-200 group-hover:scale-110" data-id="element-135" data-mp="3rtejmft45qgm">
        <PhoneIcon className="h-6 w-6 text-paper" data-id="element-136" data-mp="3hyv19aj4ml07" />
      </span>
    </Link>;
}